import { LinearProgress, Tabs } from "@material-ui/core";
import * as React from "react";

import { DesktopPageActionsProps, DesktopPageSectionProps } from ".";
import { Loading, useRelodeAppUI } from "../";
import { TabItem } from "../tab";
import { useDesktopStyles } from "./useDesktopStyles";

interface DesktopPageProps {
    children?: React.ReactNode;
    actions?: React.ReactElement<DesktopPageActionsProps>;
    isBusy?: boolean;
    busyType?: "bar" | "overlay" | "both";
    pageTitle?: string;
}

export const DesktopPage = (props: DesktopPageProps) => {
    const {
        children,
        actions,
        busyType = "both",
        isBusy = false,
        pageTitle,
        ...others
    } = props;
    const classes = useDesktopStyles(props);
    const { ui } = useRelodeAppUI();

    React.useEffect(() => {
        ui.actions.setPageTitle(pageTitle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageTitle]);

    return (
        <div className={classes.root} {...others}>
            {!!actions ? (
                <div className={classes.headerWrapper}>{actions}</div>
            ) : null}
            {isBusy ? (
                <LinearProgress
                    color="primary"
                    classes={{ root: classes.busy }}
                />
            ) : (
                <div className={classes.notBusy} />
            )}
            <Loading
                isLoading={isBusy && busyType !== "bar"}
                variant="blur"
                showIndicator={busyType !== "bar"}
                fillSpace
            >
                <div className={classes.scrollWrapper}>
                    <div className={classes.scroller}>
                        <div className={classes.contentWrapper}>{children}</div>
                    </div>
                </div>
            </Loading>
        </div>
    );
};

interface DesktopContentProps {
    defaultSection?: string;
    children?: React.ReactNode;
}
export const DesktopTabbedContent = (props: DesktopContentProps) => {
    const classes = useDesktopStyles(props);
    const { children, defaultSection, ...others } = props;
    const [selectedSection, setSelectedSection] =
        React.useState(defaultSection);

    const renderTabs = () =>
        React.Children.map(
            props.children,
            (
                child: React.ReactElement<DesktopPageSectionProps>,
                index: number
            ) => {
                if (child == null) return;
                const { label, id, count, tabIndicator } = child.props;
                return (
                    <TabItem
                        data-cy={`${label}Tab`}
                        id={"SectionTab-" + id}
                        key={index}
                        label={label}
                        value={id}
                        count={count}
                        tabIndicator={tabIndicator}
                    ></TabItem>
                );
            }
        );

    const renderSections = () =>
        React.Children.map(
            children,
            (child: React.ReactElement<DesktopPageSectionProps>) => {
                if (child == null) return;
                const { id } = child.props;
                return selectedSection === id ? child : null;
            }
        );
    return (
        <div className={classes.mainContent} {...others}>
            <Tabs
                value={selectedSection}
                onChange={(e, v) => setSelectedSection(v)}
                scrollButtons="auto"
                variant="scrollable"
                classes={{ flexContainer: classes.tabs }}
            >
                {renderTabs()}
            </Tabs>

            <div className={classes.mainContentWrapper}>{renderSections()}</div>
        </div>
    );
};

export const DesktopSideBar = (props: { children: React.ReactNode }) => {
    const { children } = props;
    const classes = useDesktopStyles(props);
    return <div className={classes.sideBar}>{children}</div>;
};

export const DesktopSideBarPane = (props: { children: React.ReactNode }) => {
    const { children } = props;
    const classes = useDesktopStyles(props);
    return <div className={classes.sideBarPane}>{children}</div>;
};
